import type { Board } from 'jsxgraph';
import type { CurveArgs, FunctionGraphCurveArgs, ParametricCurveArgs, DataCurveArgs } from '@examdojo/core/widget';

export interface IntersectionPoint {
  x: number;
  y: number;
}

/**
 * Compute intersections between a vertical line at x=lineX and a curve.
 * Used for the vertical line test: if more than one intersection, the curve is not a function.
 */
export function computeIntersections(curve: CurveArgs, lineX: number, board: Board): IntersectionPoint[] {
  switch (curve.type) {
    case 'functiongraph':
      return computeFunctionGraphIntersections(curve, lineX, board);
    case 'parametric':
      return computeParametricIntersections(curve, lineX);
    case 'data':
      return computeDataIntersections(curve, lineX);
  }
}

/**
 * Compute intersections between a horizontal line at y=lineY and a curve.
 * Used for the horizontal line test: if more than one intersection, the function is not one-to-one.
 */
export function computeHorizontalIntersections(curve: CurveArgs, lineY: number, board: Board): IntersectionPoint[] {
  switch (curve.type) {
    case 'functiongraph':
      return computeFunctionGraphHorizontalIntersections(curve, lineY, board);
    case 'parametric':
      return computeParametricHorizontalIntersections(curve, lineY);
    case 'data':
      return computeDataHorizontalIntersections(curve, lineY);
  }
}

// =============================================================================
// BISECTION
// =============================================================================

function bisect(f: (x: number) => number, a: number, b: number, maxIter = 50, tolerance = 1e-12): number {
  let lo = a;
  let hi = b;
  let fLo = f(lo);

  for (let i = 0; i < maxIter; i++) {
    const mid = (lo + hi) / 2;
    const fMid = f(mid);

    if (Math.abs(fMid) < tolerance || (hi - lo) / 2 < tolerance) {
      return mid;
    }

    if (fLo * fMid < 0) {
      hi = mid;
    } else {
      lo = mid;
      fLo = fMid;
    }
  }

  return (lo + hi) / 2;
}

// =============================================================================
// FUNCTION GRAPH: y = f(x)
// =============================================================================

/**
 * For a function graph y = f(x), the vertical line x = lineX always intersects
 * at exactly one point: (lineX, f(lineX)), provided lineX is within the domain.
 * Returns empty if lineX is outside the domain or if the value is non-finite.
 */
function computeFunctionGraphIntersections(
  curve: FunctionGraphCurveArgs,
  lineX: number,
  board: Board,
): IntersectionPoint[] {
  const bbox = board.getBoundingBox(); // [xMin, yMax, xMax, yMin]
  const domainMin = curve.domain
    ? typeof curve.domain[0] === 'function'
      ? curve.domain[0]()
      : curve.domain[0]
    : bbox[0];
  const domainMax = curve.domain
    ? typeof curve.domain[1] === 'function'
      ? curve.domain[1]()
      : curve.domain[1]
    : bbox[2];

  if (lineX < domainMin || lineX > domainMax) {
    return [];
  }

  const y = curve.fn(lineX);

  if (!isFinite(y)) {
    return [];
  }

  return [{ x: lineX, y }];
}

// =============================================================================
// PARAMETRIC: x(t), y(t)
// =============================================================================

/**
 * Scan the parameter range for sign changes in g(t) = x(t) - lineX, then refine via bisection.
 * Each root t gives an intersection point (lineX, y(t)).
 */
function computeParametricIntersections(curve: ParametricCurveArgs, lineX: number): IntersectionPoint[] {
  const g = (t: number) => curve.x(t) - lineX;
  const results: IntersectionPoint[] = [];
  const NUM_SAMPLES = 500;
  const [tMin, tMax] = curve.range;
  const step = (tMax - tMin) / NUM_SAMPLES;

  if (step <= 0) {
    return results;
  }

  let prevT = tMin;
  let prevG = g(prevT);

  for (let i = 1; i <= NUM_SAMPLES; i++) {
    const t = tMin + i * step;
    const gt = g(t);

    if (Math.abs(gt) < 1e-10) {
      results.push({ x: lineX, y: curve.y(t) });
      prevT = t;
      prevG = gt;
      continue;
    }

    if (prevG * gt < 0) {
      const rootT = bisect(g, prevT, t);
      results.push({ x: lineX, y: curve.y(rootT) });
    }

    prevT = t;
    prevG = gt;
  }

  return deduplicatePoints(results);
}

// =============================================================================
// DATA: xData[], yData[]
// =============================================================================

/**
 * Find segments where xData crosses lineX and interpolate the y value.
 */
function computeDataIntersections(curve: DataCurveArgs, lineX: number): IntersectionPoint[] {
  const results: IntersectionPoint[] = [];
  const { xData, yData } = curve;

  for (let i = 0; i < xData.length - 1; i++) {
    const x0 = xData[i] - lineX;
    const x1 = xData[i + 1] - lineX;

    if (Math.abs(x0) < 1e-10) {
      results.push({ x: lineX, y: yData[i] });
      continue;
    }

    if (x0 * x1 < 0) {
      const t = x0 / (x0 - x1);
      const y = yData[i] + t * (yData[i + 1] - yData[i]);
      results.push({ x: lineX, y });
    }
  }

  // Check last point
  if (xData.length > 0 && Math.abs(xData[xData.length - 1] - lineX) < 1e-10) {
    results.push({ x: lineX, y: yData[yData.length - 1] });
  }

  return deduplicatePoints(results);
}

// =============================================================================
// FUNCTION GRAPH: y = f(x) — HORIZONTAL
// =============================================================================

/**
 * For a function graph y = f(x), find all x where f(x) = lineY.
 * Scans the domain for sign changes in g(x) = f(x) - lineY and refines via bisection.
 */
function computeFunctionGraphHorizontalIntersections(
  curve: FunctionGraphCurveArgs,
  lineY: number,
  board: Board,
): IntersectionPoint[] {
  const bbox = board.getBoundingBox();
  const domainMin = curve.domain
    ? typeof curve.domain[0] === 'function'
      ? curve.domain[0]()
      : curve.domain[0]
    : bbox[0];
  const domainMax = curve.domain
    ? typeof curve.domain[1] === 'function'
      ? curve.domain[1]()
      : curve.domain[1]
    : bbox[2];

  const g = (x: number) => curve.fn(x) - lineY;
  const results: IntersectionPoint[] = [];
  const NUM_SAMPLES = 500;
  const step = (domainMax - domainMin) / NUM_SAMPLES;

  if (step <= 0) {
    return results;
  }

  let prevX = domainMin;
  let prevG = g(prevX);

  for (let i = 1; i <= NUM_SAMPLES; i++) {
    const x = domainMin + i * step;
    const gx = g(x);

    if (Math.abs(gx) < 1e-10) {
      results.push({ x, y: lineY });
      prevX = x;
      prevG = gx;
      continue;
    }

    if (prevG * gx < 0) {
      const rootX = bisect(g, prevX, x);
      results.push({ x: rootX, y: lineY });
    }

    prevX = x;
    prevG = gx;
  }

  return deduplicatePointsByX(results);
}

// =============================================================================
// PARAMETRIC: x(t), y(t) — HORIZONTAL
// =============================================================================

/**
 * Scan the parameter range for sign changes in g(t) = y(t) - lineY, then refine via bisection.
 * Each root t gives an intersection point (x(t), lineY).
 */
function computeParametricHorizontalIntersections(curve: ParametricCurveArgs, lineY: number): IntersectionPoint[] {
  const g = (t: number) => curve.y(t) - lineY;
  const results: IntersectionPoint[] = [];
  const NUM_SAMPLES = 500;
  const [tMin, tMax] = curve.range;
  const step = (tMax - tMin) / NUM_SAMPLES;

  if (step <= 0) {
    return results;
  }

  let prevT = tMin;
  let prevG = g(prevT);

  for (let i = 1; i <= NUM_SAMPLES; i++) {
    const t = tMin + i * step;
    const gt = g(t);

    if (Math.abs(gt) < 1e-10) {
      results.push({ x: curve.x(t), y: lineY });
      prevT = t;
      prevG = gt;
      continue;
    }

    if (prevG * gt < 0) {
      const rootT = bisect(g, prevT, t);
      results.push({ x: curve.x(rootT), y: lineY });
    }

    prevT = t;
    prevG = gt;
  }

  return deduplicatePointsByX(results);
}

// =============================================================================
// DATA: xData[], yData[] — HORIZONTAL
// =============================================================================

/**
 * Find segments where yData crosses lineY and interpolate the x value.
 */
function computeDataHorizontalIntersections(curve: DataCurveArgs, lineY: number): IntersectionPoint[] {
  const results: IntersectionPoint[] = [];
  const { xData, yData } = curve;

  for (let i = 0; i < yData.length - 1; i++) {
    const y0 = yData[i] - lineY;
    const y1 = yData[i + 1] - lineY;

    if (Math.abs(y0) < 1e-10) {
      results.push({ x: xData[i], y: lineY });
      continue;
    }

    if (y0 * y1 < 0) {
      const t = y0 / (y0 - y1);
      const x = xData[i] + t * (xData[i + 1] - xData[i]);
      results.push({ x, y: lineY });
    }
  }

  // Check last point
  if (yData.length > 0 && Math.abs(yData[yData.length - 1] - lineY) < 1e-10) {
    results.push({ x: xData[xData.length - 1], y: lineY });
  }

  return deduplicatePointsByX(results);
}

// =============================================================================
// GLOBAL FUNCTION TEST
// =============================================================================

/**
 * Determine globally whether a curve represents a function (passes the vertical line test).
 *
 * - `functiongraph`: always a function by definition (y = f(x)).
 * - `parametric`: a function iff x(t) is monotonic (no direction reversals).
 * - `data`: a function iff xData is monotonic.
 */
export function isCurveAFunction(curve: CurveArgs): boolean {
  switch (curve.type) {
    case 'functiongraph':
      return true;
    case 'parametric':
      return isParametricMonotonic(curve);
    case 'data':
      return isDataMonotonic(curve);
  }
}

function isParametricMonotonic(curve: ParametricCurveArgs): boolean {
  const NUM_SAMPLES = 1000;
  const [tMin, tMax] = curve.range;
  const step = (tMax - tMin) / NUM_SAMPLES;

  if (step <= 0) {
    return true;
  }

  let prevX = curve.x(tMin);
  let direction = 0; // 0 = unknown, 1 = increasing, -1 = decreasing

  for (let i = 1; i <= NUM_SAMPLES; i++) {
    const t = tMin + i * step;
    const x = curve.x(t);
    const dx = x - prevX;

    if (Math.abs(dx) > 1e-10) {
      const newDir = dx > 0 ? 1 : -1;
      if (direction !== 0 && newDir !== direction) {
        return false;
      }
      direction = newDir;
    }

    prevX = x;
  }

  return true;
}

function isDataMonotonic(curve: DataCurveArgs): boolean {
  const { xData } = curve;

  if (xData.length <= 1) {
    return true;
  }

  let direction = 0;

  for (let i = 1; i < xData.length; i++) {
    const dx = xData[i] - xData[i - 1];
    if (Math.abs(dx) > 1e-10) {
      const newDir = dx > 0 ? 1 : -1;
      if (direction !== 0 && newDir !== direction) {
        return false;
      }
      direction = newDir;
    }
  }

  return true;
}

// =============================================================================
// GLOBAL ONE-TO-ONE TEST (HORIZONTAL LINE TEST)
// =============================================================================

/**
 * Determine globally whether a curve is one-to-one (passes the horizontal line test).
 *
 * - `functiongraph`: one-to-one iff f(x) is monotonic over the domain.
 * - `parametric`: one-to-one iff y(t) is monotonic.
 * - `data`: one-to-one iff yData is monotonic.
 */
export function isCurveOneToOne(curve: CurveArgs, board: Board): boolean {
  switch (curve.type) {
    case 'functiongraph':
      return isFunctionGraphMonotonic(curve, board);
    case 'parametric':
      return isParametricYMonotonic(curve);
    case 'data':
      return isDataYMonotonic(curve);
  }
}

function isFunctionGraphMonotonic(curve: FunctionGraphCurveArgs, board: Board): boolean {
  const bbox = board.getBoundingBox();
  const domainMin = curve.domain
    ? typeof curve.domain[0] === 'function'
      ? curve.domain[0]()
      : curve.domain[0]
    : bbox[0];
  const domainMax = curve.domain
    ? typeof curve.domain[1] === 'function'
      ? curve.domain[1]()
      : curve.domain[1]
    : bbox[2];

  const NUM_SAMPLES = 1000;
  const step = (domainMax - domainMin) / NUM_SAMPLES;

  if (step <= 0) {
    return true;
  }

  let prevY = curve.fn(domainMin);
  let direction = 0;

  for (let i = 1; i <= NUM_SAMPLES; i++) {
    const x = domainMin + i * step;
    const y = curve.fn(x);
    const dy = y - prevY;

    if (Math.abs(dy) > 1e-10) {
      const newDir = dy > 0 ? 1 : -1;
      if (direction !== 0 && newDir !== direction) {
        return false;
      }
      direction = newDir;
    }

    prevY = y;
  }

  return true;
}

function isParametricYMonotonic(curve: ParametricCurveArgs): boolean {
  const NUM_SAMPLES = 1000;
  const [tMin, tMax] = curve.range;
  const step = (tMax - tMin) / NUM_SAMPLES;

  if (step <= 0) {
    return true;
  }

  let prevY = curve.y(tMin);
  let direction = 0;

  for (let i = 1; i <= NUM_SAMPLES; i++) {
    const t = tMin + i * step;
    const y = curve.y(t);
    const dy = y - prevY;

    if (Math.abs(dy) > 1e-10) {
      const newDir = dy > 0 ? 1 : -1;
      if (direction !== 0 && newDir !== direction) {
        return false;
      }
      direction = newDir;
    }

    prevY = y;
  }

  return true;
}

function isDataYMonotonic(curve: DataCurveArgs): boolean {
  const { yData } = curve;

  if (yData.length <= 1) {
    return true;
  }

  let direction = 0;

  for (let i = 1; i < yData.length; i++) {
    const dy = yData[i] - yData[i - 1];
    if (Math.abs(dy) > 1e-10) {
      const newDir = dy > 0 ? 1 : -1;
      if (direction !== 0 && newDir !== direction) {
        return false;
      }
      direction = newDir;
    }
  }

  return true;
}

// =============================================================================
// DEDUPLICATION
// =============================================================================

/** Remove near-duplicate intersection points (within tolerance on y-coordinate). */
function deduplicatePoints(points: IntersectionPoint[], tolerance = 1e-6): IntersectionPoint[] {
  if (points.length <= 1) {
    return points;
  }

  const sorted = [...points].sort((a, b) => a.y - b.y);
  const result: IntersectionPoint[] = [sorted[0]];

  for (let i = 1; i < sorted.length; i++) {
    if (Math.abs(sorted[i].y - result[result.length - 1].y) > tolerance) {
      result.push(sorted[i]);
    }
  }

  return result;
}

/** Remove near-duplicate intersection points (within tolerance on x-coordinate). */
function deduplicatePointsByX(points: IntersectionPoint[], tolerance = 1e-6): IntersectionPoint[] {
  if (points.length <= 1) {
    return points;
  }

  const sorted = [...points].sort((a, b) => a.x - b.x);
  const result: IntersectionPoint[] = [sorted[0]];

  for (let i = 1; i < sorted.length; i++) {
    if (Math.abs(sorted[i].x - result[result.length - 1].x) > tolerance) {
      result.push(sorted[i]);
    }
  }

  return result;
}
